import { HttpClient } from '@angular/common/http';
import { IirdsContainer, IirdsContainerBuilder } from '@enduradocs/iirds';
import { ConfigService } from '@rxap/config';
import { CID } from 'multiformats';
import { CidDirectory } from '../fs/cid/cid-directory';
import { HttpDirectory } from '../fs/http/http-directory';
import { isHeliaEnabled } from '../is-helia-enabled';
import { FetchCidContentMethod } from '../methods/fetch-cid-content.method';
import { normalizeIirdsContainer } from './normalize-iirds-container';

/**
 * Loads and builds an iiRDS container directly from a CID, independent of
 * whichever source/container is currently active in the store. Mirrors the
 * `cid` branch of `iirdsGuard.tryLoad` (Helia verified-fetch when enabled,
 * else the `<cid>.<gatewayDomain>` HTTPS gateway) so any code that needs
 * *another* archive's content — e.g. building a search index for every
 * configured product — can reuse the same loading path without depending on
 * the active store's source-selection state.
 */
export async function loadIirdsContainerFromCid(
  cid: CID | string,
  config: ConfigService,
  fetchCidContentMethod: FetchCidContentMethod,
  http: HttpClient
): Promise<IirdsContainer> {
  const cidStr = typeof cid === 'string' ? cid : cid.toV1().toString();

  const directory: CidDirectory | HttpDirectory = isHeliaEnabled(config)
    ? new CidDirectory(cidStr, fetchCidContentMethod, '', '')
    : new HttpDirectory(
        `https://${cidStr}.${config.get<string>(
          'ipfsGatewayDomain',
          `ipfs.${location.hostname}`
        )}`,
        http,
        '',
        ''
      );
  directory.registerFile('META-INF/metadata.rdf', 'application/rdf+xml');

  const content = await directory
    .directory('META-INF')
    .file('metadata.rdf')
    .getContent('application/rdf+xml');
  if (!(content.includes('<rdf:RDF') || content.includes('<rdf:rdf'))) {
    throw new Error('The metadata.rdf does not contain RDF data');
  }

  const builder = new IirdsContainerBuilder(
    window.DOMParser,
    window.XMLSerializer,
    window.TextEncoder,
    window.TextDecoder
  );
  const container = await builder.build(directory);
  normalizeIirdsContainer(container);
  container.metadata.rdf.setDirectory(directory);
  container.metadata.rdf.setCid(cidStr);
  container.metadata.rdf.renditionList.forEach((rendition) => {
    directory.registerFile(rendition.source, rendition.format);
  });

  return container;
}
